/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import { Container } from 'inversify'
import { ErrorPlatformEvent, interpret } from 'xstate-v4'
import { rootContainer } from '~/container'
import sentryCapture from '~/utility/sentryCapture'
import {
  fetchImageRepositoryFactory,
  IImageRepositoryFactory,
  ImageRepositoryFactory,
  ImageUploaderMachineInterpreterFactory,
} from './factories'
import {
  buildImageUploaderMachine,
  ImageUploaderContext,
  ImageUploaderEvents,
} from './machines/ImageUploaderMachine'
import { returnUploadedImagePreviewMachine } from './machines/UploadedImagePreviewMachine'

const imageUploaderContainer = new Container()
imageUploaderContainer.parent = rootContainer

function errorHandlerWrapper(name: string) {
  return function errorHandler(_: unknown, _event: any) {
    const event: ErrorPlatformEvent = _event
    event.data.name = `${name} ${event.type} error`
    sentryCapture(event.data)
  }
}

imageUploaderContainer
  .bind<ImageRepositoryFactory>(IImageRepositoryFactory)
  .toFactory(fetchImageRepositoryFactory)
imageUploaderContainer
  .bind<ImageUploaderMachineInterpreterFactory>(
    'ImageUploaderMachineInterpreterFactory',
  )
  .toDynamicValue((ctx) => {
    return (campsiteSlug: string, token: string, langCode: string) => {
      const repo = ctx.container.get<ImageRepositoryFactory>(
        IImageRepositoryFactory,
      )(campsiteSlug, token, langCode)
      const uploadedImagePreviewMachineConfig = {
        actions: {
          logError: errorHandlerWrapper('uploadedImagePreviewMachine'),
        },
        services: {
          getImageStatus: ({ image }) => repo.readOneById(image.id),
          removeImage: ({ image }) => repo.delete(image),
          updateImage: ({ image }) => repo.update(image),
        },
      }
      const imageUploaderServices = {
        getAllImages: () => repo.getAllImages(),
        uploadImages: async (
          _: ImageUploaderContext,
          event: ImageUploaderEvents,
        ) => {
          return event.type === 'NEW_IMAGES'
            ? await repo.createMany(event.filestackImages)
            : []
        },
      }
      return interpret(
        buildImageUploaderMachine(
          imageUploaderServices,
          returnUploadedImagePreviewMachine().withConfig(
            uploadedImagePreviewMachineConfig,
          ),
          { logError: errorHandlerWrapper('imageUploaderMachine') },
        ).withContext({
          campsiteSlug,
          token,
          imagesPreviews: {},
        }),
      )
    }
  })

export function getImageUploaderInterpreterFactory() {
  return imageUploaderContainer.get<ImageUploaderMachineInterpreterFactory>(
    'ImageUploaderMachineInterpreterFactory',
  )
}
